import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Home, Search, Filter, Building, Plus, X, Edit, User, Loader2 } from 'lucide-react';
import Pagination from '../components/Pagination';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/*
 * Altam™ Operating Services
 * Units Management - Supports Rental Units and Condo Units
 * Copyright (c) 2026 Pecaro Consulting Inc. All rights reserved.
 */

export default function Units() {
  const [units, setUnits] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterProperty, setFilterProperty] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const [formData, setFormData] = useState({
    entity_id: '', unit_number: '', floor: 1, type: 'apartment',
    bedrooms: 1, bathrooms: 1, square_feet: 800, monthly_rent: '', status: 'vacant',
    // Owner fields for condo units
    owner_name: '', owner_email: '', owner_phone: ''
  });

  useEffect(() => { fetchProperties(); }, []);

  useEffect(() => { fetchUnits(); }, [pagination.page, filterProperty, filterStatus, filterType, searchTerm]);

  const fetchProperties = async () => {
    try {
      const res = await axios.get(`${API}/corporations`);
      setProperties(res.data);
    } catch (error) { console.error('Failed to fetch properties:', error); }
  };

  const fetchUnits = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', pagination.page);
      params.append('limit', pagination.limit);
      if (filterProperty) params.append('entity_id', filterProperty);
      if (filterStatus) params.append('status', filterStatus);
      if (filterType) params.append('type', filterType);
      if (searchTerm) params.append('search', searchTerm);
      
      const res = await axios.get(`${API}/units?${params}`);
      
      // Handle both paginated and non-paginated responses
      if (res.data.data) {
        setUnits(res.data.data);
        setPagination(prev => ({ ...prev, ...res.data.pagination }));
      } else {
        // Fallback for non-paginated response
        setUnits(res.data);
        setPagination(prev => ({ ...prev, total: res.data.length, pages: 1 }));
      }
    } catch (error) { console.error('Failed to fetch units:', error); }
    finally { setLoading(false); }
  }, [pagination.page, pagination.limit, filterProperty, filterStatus, filterType, searchTerm]);

  // Get property type to determine if it's a condo
  const getPropertyType = (entityId) => {
    const property = properties.find(p => p.id === entityId);
    return property?.type?.toLowerCase() || '';
  };

  const isCondoProperty = (entityId) => {
    return getPropertyType(entityId) === 'condo';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { 
        ...formData, 
        monthly_rent: parseFloat(formData.monthly_rent) || 0,
        monthly_fee: parseFloat(formData.monthly_rent) || 0 // Alias for condo
      };
      if (editingUnit) {
        await axios.patch(`${API}/units/${editingUnit.id}`, data);
      } else {
        await axios.post(`${API}/units`, data);
      }
      setShowModal(false);
      resetForm();
      fetchUnits();
    } catch (error) { console.error('Failed to save unit:', error); }
  };

  const openEditModal = (unit) => {
    setEditingUnit(unit);
    setFormData({
      entity_id: unit.entity_id || '', unit_number: unit.unit_number || '', floor: unit.floor || 1,
      type: unit.type || 'apartment', bedrooms: unit.bedrooms || 1, bathrooms: unit.bathrooms || 1,
      square_feet: unit.square_feet || 800, monthly_rent: unit.monthly_rent || unit.monthly_fee || '', 
      status: unit.status || 'vacant',
      owner_name: unit.owner_name || '', owner_email: unit.owner_email || '', owner_phone: unit.owner_phone || ''
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingUnit(null);
    setFormData({ 
      entity_id: '', unit_number: '', floor: 1, type: 'apartment', bedrooms: 1, bathrooms: 1, 
      square_feet: 800, monthly_rent: '', status: 'vacant',
      owner_name: '', owner_email: '', owner_phone: ''
    });
  };

  const handleFilterChange = (type, value) => {
    if (type === 'property') setFilterProperty(value);
    if (type === 'status') setFilterStatus(value);
    if (type === 'type') setFilterType(value);
    if (type === 'search') setSearchTerm(value);
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to page 1 when filtering
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  // Use units directly since filtering is now done server-side
  const filteredUnits = units;

  const formatCurrency = (amount) => amount ? new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 0 }).format(amount) : '-';

  // Check if currently selected property in form is condo
  const isCurrentFormCondo = formData.entity_id && isCondoProperty(formData.entity_id);

  if (loading && units.length === 0) { 
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    ); 
  }

  return (
    <div className="space-y-6" data-testid="units-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Units</h1>
          <p className="text-slate-500">Manage rental units and condo units across your properties</p>
        </div>
        <button onClick={() => { resetForm(); setShowModal(true); }} className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-600 to-violet-600 text-white rounded-lg hover:from-indigo-700 hover:to-violet-700" data-testid="add-unit-btn">
          <Plus className="w-4 h-4" /> Add Unit
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input type="text" placeholder="Search units, properties, owners..." value={searchTerm} onChange={(e) => handleFilterChange('search', e.target.value)} className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
        </div>
        <select value={filterProperty} onChange={(e) => handleFilterChange('property', e.target.value)} className="px-4 py-2 border border-slate-200 rounded-lg bg-white">
          <option value="">All Properties</option>
          {properties.map(p => <option key={p.id} value={p.id}>{p.name} ({p.type || p.property_type || 'property'})</option>)}
        </select>
        <select value={filterType} onChange={(e) => handleFilterChange('type', e.target.value)} className="px-4 py-2 border border-slate-200 rounded-lg bg-white">
          <option value="">All Types</option>
          <option value="condo">Condo</option>
          <option value="apartment">Apartment</option>
          <option value="commercial">Commercial</option>
        </select>
        <select value={filterStatus} onChange={(e) => handleFilterChange('status', e.target.value)} className="px-4 py-2 border border-slate-200 rounded-lg bg-white">
          <option value="">All Status</option>
          <option value="occupied">Occupied</option>
          <option value="vacant">Vacant</option>
        </select>
      </div>

      {/* Stats - Use pagination.total to reflect server-side count */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-sm text-slate-500">{filterProperty ? 'Units in Property' : 'Total Units'}</p>
          <p className="text-2xl font-bold text-slate-900">{pagination.total}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-sm text-slate-500">Occupied</p>
          <p className="text-2xl font-bold text-emerald-600">{filteredUnits.filter(u => u.status === 'occupied').length}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-sm text-slate-500">Vacant</p>
          <p className="text-2xl font-bold text-rose-600">{filteredUnits.filter(u => u.status === 'vacant').length}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-sm text-slate-500">Condo Units</p>
          <p className="text-2xl font-bold text-violet-600">{filteredUnits.filter(u => u.type === 'condo').length}</p>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
          </div>
        ) : (
        <>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Unit</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Property</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Owner/Tenant</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Details</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Monthly</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {filteredUnits.map((unit) => {
                const isCondo = unit.type === 'condo';
                return (
                  <tr key={unit.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${isCondo ? 'bg-emerald-100' : 'bg-indigo-100'}`}>
                          <Home className={`w-4 h-4 ${isCondo ? 'text-emerald-600' : 'text-indigo-600'}`} />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{unit.unit_number}</p>
                          <p className="text-xs text-slate-500">Floor {unit.floor}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-900">{unit.entity_name}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded-full capitalize ${
                        isCondo ? 'bg-emerald-100 text-emerald-700' : 'bg-indigo-100 text-indigo-700'
                      }`}>
                        {unit.type}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {unit.owner_name ? (
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-slate-400" />
                          <div>
                            <p className="text-sm font-medium text-slate-900">{unit.owner_name}</p>
                            <p className="text-xs text-slate-500">{unit.owner_email}</p>
                          </div>
                        </div>
                      ) : (
                        <span className="text-sm text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {unit.bedrooms > 0 && `${unit.bedrooms} bed • `}{unit.bathrooms} bath • {unit.square_feet} sqft
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-slate-900">{formatCurrency(unit.monthly_rent || unit.monthly_fee || unit.monthly_maintenance_fee)}</p>
                        <p className="text-xs text-slate-500">{isCondo ? 'Maint. Fee' : 'Rent'}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        unit.status === 'occupied' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'
                      }`}>
                        {unit.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button onClick={() => openEditModal(unit)} className="p-1 text-indigo-600 hover:bg-indigo-50 rounded" data-testid={`edit-unit-${unit.id}`}>
                        <Edit className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filteredUnits.length === 0 && (
            <div className="py-12 text-center text-slate-500">
              No units found matching your criteria
            </div>
          )}
        </div>
        <Pagination pagination={pagination} onPageChange={handlePageChange} />
        </>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">{editingUnit ? 'Edit Unit' : 'Add Unit'}</h3>
              <button onClick={() => setShowModal(false)} className="p-1 hover:bg-slate-100 rounded">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Property & Unit Number */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Property *</label>
                  <select required value={formData.entity_id} onChange={(e) => setFormData({...formData, entity_id: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg">
                    <option value="">Select property</option>
                    {properties.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.name} ({p.type})
                      </option>
                    ))}
                  </select>
                  {formData.entity_id && (
                    <p className={`text-xs mt-1 ${isCurrentFormCondo ? 'text-emerald-600' : 'text-indigo-600'}`}>
                      {isCurrentFormCondo ? '🏠 Condo Property - Owner info required' : '🏢 Rental Property - Lease required'}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Unit Number *</label>
                  <input type="text" required value={formData.unit_number} onChange={(e) => setFormData({...formData, unit_number: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg" placeholder="e.g., 101" />
                </div>
              </div>

              {/* Unit Details */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Floor</label>
                  <input type="number" value={formData.floor} onChange={(e) => setFormData({...formData, floor: parseInt(e.target.value)})} className="w-full px-3 py-2 border border-slate-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Type</label>
                  <select value={formData.type} onChange={(e) => setFormData({...formData, type: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg">
                    <option value="condo">Condo</option>
                    <option value="apartment">Apartment</option>
                    <option value="townhouse">Townhouse</option>
                    <option value="studio">Studio</option>
                    <option value="commercial">Commercial</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
                  <select value={formData.status} onChange={(e) => setFormData({...formData, status: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg">
                    <option value="vacant">Vacant</option>
                    <option value="occupied">Occupied</option>
                  </select>
                </div>
              </div>

              {/* Size & Rooms */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Bedrooms</label>
                  <input type="number" min="0" value={formData.bedrooms} onChange={(e) => setFormData({...formData, bedrooms: parseInt(e.target.value)})} className="w-full px-3 py-2 border border-slate-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Bathrooms</label>
                  <input type="number" min="1" step="0.5" value={formData.bathrooms} onChange={(e) => setFormData({...formData, bathrooms: parseFloat(e.target.value)})} className="w-full px-3 py-2 border border-slate-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Sq Ft</label>
                  <input type="number" value={formData.square_feet} onChange={(e) => setFormData({...formData, square_feet: parseInt(e.target.value)})} className="w-full px-3 py-2 border border-slate-300 rounded-lg" />
                </div>
              </div>

              {/* Monthly Fee */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {isCurrentFormCondo ? 'Monthly Maintenance Fee' : 'Monthly Rent'} *
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                  <input type="number" step="0.01" required value={formData.monthly_rent} onChange={(e) => setFormData({...formData, monthly_rent: e.target.value})} className="w-full pl-8 pr-4 py-2 border border-slate-300 rounded-lg" placeholder="0.00" />
                </div>
              </div>

              {/* Owner Info - Only for Condo */}
              {(isCurrentFormCondo || formData.type === 'condo') && (
                <div className="border-t border-slate-200 pt-4 mt-4">
                  <h4 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <User className="w-4 h-4 text-emerald-600" />
                    Owner Information
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Owner Name *</label>
                      <input type="text" required={isCurrentFormCondo} value={formData.owner_name} onChange={(e) => setFormData({...formData, owner_name: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg" placeholder="John Smith" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Owner Email *</label>
                        <input type="email" required={isCurrentFormCondo} value={formData.owner_email} onChange={(e) => setFormData({...formData, owner_email: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg" placeholder="owner@example.com" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Owner Phone</label>
                        <input type="tel" value={formData.owner_phone} onChange={(e) => setFormData({...formData, owner_phone: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg" placeholder="(416) 555-1234" />
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    ℹ️ Owner email is used for portal access and fee collection
                  </p>
                </div>
              )}

              {/* Rental Note */}
              {!isCurrentFormCondo && formData.entity_id && (
                <div className="bg-indigo-50 rounded-lg p-3 text-sm text-indigo-700">
                  ℹ️ For rental units, create a <strong>Lease</strong> after adding the unit to assign a tenant and set up rent collection.
                </div>
              )}

              {/* Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50">
                  Cancel
                </button>
                <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
                  {editingUnit ? 'Save Changes' : 'Add Unit'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
